import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  MenuItem,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Tooltip,
  useTheme,
  alpha,
  Avatar,
  Divider,
  CircularProgress
} from '@mui/material';
import {
  Visibility as ViewIcon,
  GetApp as DownloadIcon,
  CheckCircle as ApproveIcon,
  CheckCircle,
  Assignment as AssignmentIcon,
  Search as SearchIcon,
  Psychology as AIIcon,
  SmartToy as SmartToyIcon
} from '@mui/icons-material';
import { supabase } from '../../lib/supabaseClient';
import { getAuthUser } from '../../services/authService';
import { useEntity } from '../../context/EntityContext';
import { parseResumeWithAI, matchResumeWithJD, isAIEnabled, AIMatchingResult } from '../../services/aiResumeParserService';
import { AIResumeAnalysis } from '../../components/AIResumeAnalysis';
import { initiateAICall, formatPhoneNumber, fetchAndSaveCallStatus } from '../../services/elevenLabsService';
import { getResumeUrl } from '../../services/storageService';
import { addStatusChangeNote, addApplicationNote } from '../../services/applicationService';
import { TableSkeleton, EmptyState, LoadingButton, useToast } from '../../components/ui';
import { NotesChat } from '../../components/NotesChat';

interface SkillMatch {
  skill: string;
  required: boolean;
  matched: boolean;
  score: number;
}

interface ScreeningResults {
  overallScore?: number;
  skillMatches?: SkillMatch[];
  experienceMatch?: number;
  educationMatch?: number;
  keywords?: string[];
  // Bulk upload structure
  matchResult?: {
    overallScore: number;
    skillMatches?: SkillMatch[];
    experienceMatch?: number;
    educationMatch?: number;
    strengths?: string[];
    weaknesses?: string[];
    recommendations?: string[];
  };
  parsedResume?: any;
  skills?: string[];
  experienceYears?: number;
  education?: any[];
  resumeText?: string;
}

interface Application {
  id: string;
  jobId: string;
  jobTitle: string;
  jobStatus: 'active' | 'paused' | 'closed' | 'expired';
  applicantName: string;
  applicantEmail: string;
  applicantPhone?: string;
  resumeUrl: string;
  coverLetter?: string;
  status: 'pending' | 'reviewing' | 'shortlisted' | 'rejected' | 'hired';
  matchScore?: number;
  screeningResults?: ScreeningResults;
  aiAnalysisResults?: AIMatchingResult;
  submittedAt: string;
  reviewedBy?: string;
  reviewedAt?: string;
  notes?: string;
}

/**
 * Get the best available match score from an application
 * Handles both frontend submissions (ai_analysis_results) and bulk uploads (screening_results.matchResult)
 */
function getApplicationScore(app: Application): number | undefined {
  // Priority 1: Database match_score column (should be set correctly for all sources)
  if (app.matchScore !== undefined && app.matchScore !== null) {
    return app.matchScore;
  }

  // Priority 2: AI analysis results (frontend submissions)
  if (app.aiAnalysisResults?.overallScore !== undefined) {
    return app.aiAnalysisResults.overallScore;
  }

  // Priority 3: Nested matchResult in screening_results (bulk uploads)
  if (app.screeningResults?.matchResult?.overallScore !== undefined) {
    return app.screeningResults.matchResult.overallScore;
  }

  // Priority 4: Direct overallScore in screening_results (local scoring fallback)
  if (app.screeningResults?.overallScore !== undefined) {
    return app.screeningResults.overallScore;
  }

  return undefined;
}

export const ApplicationsPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { showSuccess, showError: showToastError } = useToast();
  const { entity } = useEntity();
  const [applications, setApplications] = useState<Application[]>([]);
  const [filteredApplications, setFilteredApplications] = useState<Application[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedApplication, setSelectedApplication] = useState<Application | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [newStatus, setNewStatus] = useState<string>('');
  const [notes, setNotes] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [jobFilter, setJobFilter] = useState('all');
  const [jobs, setJobs] = useState<{ id: string; title: string }[]>([]);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [aiAnalysis, setAiAnalysis] = useState<AIMatchingResult | null>(null);
  const [aiLoading, setAiLoading] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [jobDetails, _setJobDetails] = useState<any>(null);
  const [aiCallLoading, setAiCallLoading] = useState(false);
  const [statusUpdateLoading, setStatusUpdateLoading] = useState(false);

  useEffect(() => {
    loadCurrentUser();
    loadJobs();
    loadApplications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entity?.id]);

  useEffect(() => {
    filterApplications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applications, selectedTab, searchTerm, jobFilter]);

  const loadCurrentUser = async () => {
    const user = await getAuthUser();
    setCurrentUser(user);
  };

  const loadJobs = async () => {
    try {
      const user = await getAuthUser();
      if (!user) return;

      const { data, error: fetchError } = await supabase
        .from('jobs')
        .select('id, title')
        .eq('entity_id', user.entityId)
        .order('title');

      if (fetchError) throw fetchError;

      setJobs(data || []);
    } catch (err: any) {
      console.error('Error loading jobs:', err);
    }
  };

  const loadApplications = async () => {
    try {
      setLoading(true);
      const user = await getAuthUser();

      if (!user) {
        setError('You must be logged in');
        return;
      }

      const { data, error: fetchError } = await supabase
        .from('applications')
        .select(`
          *,
          job:jobs!applications_job_id_fkey(title, status),
          reviewer:users!applications_reviewed_by_fkey(name)
        `)
        .eq('entity_id', user.entityId)
        .is('deleted_at', null)
        .order('submitted_at', { ascending: false });

      if (fetchError) throw fetchError;

      const formattedApplications = (data || []).map((app: any) => ({
        id: app.id,
        jobId: app.job_id,
        jobTitle: app.job?.title || 'Unknown Job',
        jobStatus: app.job?.status || 'active',
        applicantName: app.applicant_name,
        applicantEmail: app.applicant_email,
        applicantPhone: app.applicant_phone,
        resumeUrl: app.resume_url,
        coverLetter: app.cover_letter,
        status: app.status,
        matchScore: app.match_score,
        screeningResults: app.screening_results,
        aiAnalysisResults: app.ai_analysis_results,
        submittedAt: app.submitted_at,
        reviewedBy: app.reviewer?.name,
        reviewedAt: app.reviewed_at,
        notes: app.notes
      }));

      setApplications(formattedApplications);
      setError('');
    } catch (err: any) {
      console.error('Error loading applications:', err);
      setError(err.message || 'Failed to load applications');
    } finally {
      setLoading(false);
    }
  };

  const filterApplications = () => {
    let filtered = applications;

    // Filter by tab (status)
    // Tab 0: New/Reviewing - includes 'pending' (from frontend submissions) and 'reviewing' (from bulk upload)
    if (selectedTab === 0) {
      filtered = filtered.filter(app => ['pending', 'reviewing'].includes(app.status));
    } else if (selectedTab === 1) {
      filtered = filtered.filter(app => app.status === 'shortlisted');
    } else if (selectedTab === 2) {
      filtered = filtered.filter(app => app.status === 'rejected');
    } else if (selectedTab === 3) {
      filtered = filtered.filter(app => app.status === 'hired');
    }

    // Filter by job
    if (jobFilter !== 'all') {
      filtered = filtered.filter(app => app.jobId === jobFilter);
    }

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(app =>
        app.applicantName.toLowerCase().includes(term) ||
        app.applicantEmail.toLowerCase().includes(term) ||
        app.jobTitle.toLowerCase().includes(term)
      );
    }

    setFilteredApplications(filtered);
  };

  // Handle resume download with proper Supabase storage URL
  const handleDownloadResume = async (resumeUrl: string, applicantName: string) => {
    try {
      // Check if it's a bulk upload placeholder (no actual file)
      if (resumeUrl.startsWith('bulk-upload/')) {
        setError('Resume file not available for download. This application was created via bulk upload - the resume was processed but not stored.');
        return;
      }

      const url = await getResumeUrl(resumeUrl);
      if (!url) {
        setError('Resume file not available. The file may have been moved or deleted.');
        return;
      }
      // Open in new tab
      window.open(url, '_blank');
    } catch (err) {
      console.error('Error downloading resume:', err);
      setError('Failed to download resume');
    }
  };

  const handleViewApplication = (application: Application) => {
    // Navigate to the application detail page
    navigate(`/admin/applications/${application.id}`);
  };

  const handleAIAnalysis = async () => {
    if (!selectedApplication || !jobDetails) {
      setError('Missing application or job details');
      return;
    }

    if (!isAIEnabled()) {
      setError('AI analysis is not enabled. Please configure your OpenAI API key.');
      return;
    }

    setAiLoading(true);
    setError('');

    try {
      // Get proper resume URL from Supabase storage
      const resumeUrl = await getResumeUrl(selectedApplication.resumeUrl);
      if (!resumeUrl) {
        setError('Resume file not available');
        setAiLoading(false);
        return;
      }

      // Fetch the resume file
      const resumeResponse = await fetch(resumeUrl);
      const resumeBlob = await resumeResponse.blob();
      const resumeFile = new File([resumeBlob], 'resume.pdf', { type: resumeBlob.type });

      // Parse resume with AI
      const parsedResume = await parseResumeWithAI(resumeFile);

      // Match with job description
      const matchResult = await matchResumeWithJD(parsedResume, {
        title: jobDetails.title,
        description: jobDetails.description,
        requirements: jobDetails.requirements || [],
        benefits: jobDetails.benefits || [],
        location: jobDetails.location,
        workType: jobDetails.work_type
      });

      setAiAnalysis(matchResult);

      // Save AI analysis results to database
      try {
        const { error: updateError } = await supabase
          .from('applications')
          .update({
            ai_analysis_results: matchResult,
            match_score: matchResult.overallScore,
            updated_at: new Date().toISOString()
          })
          .eq('id', selectedApplication.id);

        if (updateError) {
          console.error('Error saving AI analysis:', updateError);
        } else {
          // Update local application data
          setApplications(prev => prev.map(app =>
            app.id === selectedApplication.id
              ? { ...app, aiAnalysisResults: matchResult, matchScore: matchResult.overallScore }
              : app
          ));
        }
      } catch (saveError) {
        console.error('Error saving AI analysis:', saveError);
      }

      showSuccess('AI analysis completed successfully!', 'Analysis Complete');
    } catch (err: any) {
      console.error('AI analysis error:', err);
      setError(err.message || 'Failed to analyze resume with AI');
    } finally {
      setAiLoading(false);
    }
  };

  const handleStatusChange = (application: Application) => {
    setSelectedApplication(application);
    setNewStatus(application.status);
    setNotes(application.notes || '');
    setStatusDialogOpen(true);
  };

  const handleAICallFromDialog = async () => {
    if (!selectedApplication || !selectedApplication.applicantPhone || !currentUser) return;

    setAiCallLoading(true);
    setError('');

    try {
      const result = await initiateAICall({
        applicationId: selectedApplication.id,
        applicantName: selectedApplication.applicantName,
        applicantPhone: selectedApplication.applicantPhone,
        jobTitle: selectedApplication.jobTitle,
        matchScore: selectedApplication.matchScore,
        entityId: currentUser.entityId,
      });

      if (result.success && result.callId) {
        showSuccess(
          `AI call initiated! The AI agent will call ${formatPhoneNumber(selectedApplication.applicantPhone)} shortly.`,
          'Call Started'
        );

        // Fetch call status after 10 seconds to get initial call details
        setTimeout(async () => {
          await fetchAndSaveCallStatus(
            result.callId!,
            selectedApplication.id,
            currentUser.entityId
          );
        }, 10000);
      } else {
        // Show error in the UI - don't close dialog so user can see the error
        setError(result.error || 'Failed to initiate AI call');
      }
    } catch (err: any) {
      console.error('Error initiating AI call:', err);
      setError('An unexpected error occurred while initiating the call');
    } finally {
      setAiCallLoading(false);
    }
  };

  const handleUpdateStatus = async () => {
    if (!selectedApplication || !currentUser) return;

    try {
      setStatusUpdateLoading(true);
      setError('');

      const { error: updateError } = await supabase
        .from('applications')
        .update({
          status: newStatus,
          reviewed_by: currentUser.id,
          reviewed_at: new Date().toISOString(),
          notes: notes,
          updated_at: new Date().toISOString()
        })
        .eq('id', selectedApplication.id);

      if (updateError) throw updateError;

      // Add status change audit note
      if (newStatus !== selectedApplication.status) {
        await addStatusChangeNote(
          selectedApplication.id,
          currentUser.entityId,
          currentUser.id,
          currentUser.name || currentUser.email,
          currentUser.role || 'user',
          selectedApplication.status,
          newStatus
        );
      }

      // Add user's notes text as a comment note if provided
      if (notes.trim()) {
        await addApplicationNote({
          applicationId: selectedApplication.id,
          entityId: currentUser.entityId,
          authorId: currentUser.id,
          authorName: currentUser.name || currentUser.email,
          authorRole: currentUser.role || 'user',
          content: notes.trim(),
          noteType: 'comment'
        });
      }

      showSuccess(`Application status updated to ${newStatus}`, 'Status Updated');
      setStatusDialogOpen(false);
      setSelectedApplication(null);
      loadApplications();
    } catch (err: any) {
      console.error('Error updating status:', err);
      showToastError(err.message || 'Failed to update application status');
      setError(err.message || 'Failed to update application status');
    } finally {
      setStatusUpdateLoading(false);
    }
  };

  const getStatusChip = (status: string) => {
    const statusConfig: Record<string, { label: string; color: any }> = {
      pending: { label: 'New', color: 'warning' },
      reviewing: { label: 'Under Review', color: 'info' },
      shortlisted: { label: 'Shortlisted', color: 'success' },
      rejected: { label: 'Rejected', color: 'error' },
      hired: { label: 'Hired', color: 'success' }
    };

    const config = statusConfig[status] || { label: status, color: 'default' };
    return <Chip label={config.label} color={config.color} size="small" />;
  };

  const getJobStatusChip = (status: string) => {
    const statusConfig: Record<string, { label: string; color: any }> = {
      active: { label: 'Active', color: 'success' },
      paused: { label: 'Paused', color: 'warning' },
      closed: { label: 'Closed', color: 'default' },
      expired: { label: 'Expired', color: 'error' }
    };

    const config = statusConfig[status] || { label: status, color: 'default' };
    return <Chip label={config.label} color={config.color} size="small" variant="outlined" />;
  };

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <Box sx={{ py: 4, backgroundColor: theme.palette.background.default, minHeight: '100vh' }}>
      <Container maxWidth="xl">
        {/* Header */}
        <Paper
          elevation={0}
          sx={{
            p: 4,
            mb: 4,
            bgcolor: '#ffffff',
            border: '1px solid #e0e0e0',
            color: 'text.primary',
            borderRadius: 2
          }}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <AssignmentIcon sx={{ fontSize: 40 }} />
            <Box>
              <Typography variant="h4" fontWeight={700}>
                Applications
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9, mt: 1 }}>
                Review and manage job applications
              </Typography>
            </Box>
          </Stack>
        </Paper>

        {/* Alerts - Only errors shown inline, success uses toast */}
        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Filters */}
        <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 3 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              placeholder="Search by name, email, or job..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
              sx={{ flex: 1 }}
            />

            <TextField
              select
              label="Filter by Job"
              value={jobFilter}
              onChange={(e) => setJobFilter(e.target.value)}
              sx={{ minWidth: 250 }}
            >
              <MenuItem value="all">All Jobs</MenuItem>
              {jobs.map(job => (
                <MenuItem key={job.id} value={job.id}>{job.title}</MenuItem>
              ))}
            </TextField>
          </Stack>
        </Paper>

        {/* Tabs */}
        <Paper elevation={3} sx={{ borderRadius: 4, overflow: 'hidden' }}>
          <Tabs
            value={selectedTab}
            onChange={(_, newValue) => setSelectedTab(newValue)}
            sx={{
              backgroundColor: alpha(theme.palette.primary.main, 0.05),
              '& .MuiTab-root': { fontWeight: 600 }
            }}
          >
            <Tab label={`Reviewing (${applications.filter(a => ['pending', 'reviewing'].includes(a.status)).length})`} />
            <Tab label={`Shortlisted (${applications.filter(a => a.status === 'shortlisted').length})`} />
            <Tab label={`Rejected (${applications.filter(a => a.status === 'rejected').length})`} />
            <Tab label={`Hired (${applications.filter(a => a.status === 'hired').length})`} />
          </Tabs>

          {/* Table */}
          <TableContainer>
            {loading ? (
              <Box sx={{ p: 2 }}>
                <TableSkeleton rows={5} columns={7} />
              </Box>
            ) : filteredApplications.length === 0 ? (
              <EmptyState
                variant={searchTerm || jobFilter !== 'all' ? 'search' : 'applications'}
                title={searchTerm || jobFilter !== 'all' ? 'No results found' : 'No applications yet'}
                description={
                  searchTerm || jobFilter !== 'all'
                    ? 'Try adjusting your search terms or filters'
                    : 'Applications will appear here once candidates start applying to your job postings.'
                }
                actionLabel={searchTerm || jobFilter !== 'all' ? 'Clear Filters' : undefined}
                onAction={searchTerm || jobFilter !== 'all' ? () => { setSearchTerm(''); setJobFilter('all'); } : undefined}
              />
            ) : (
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Applicant</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Job Title</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Job Status</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Match Score</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Applied</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredApplications.map((app) => (
                    <TableRow key={app.id} hover>
                      <TableCell>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
                            {getInitials(app.applicantName)}
                          </Avatar>
                          <Box>
                            <Typography fontWeight={600}>{app.applicantName}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {app.applicantEmail}
                            </Typography>
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{app.jobTitle}</Typography>
                      </TableCell>
                      <TableCell>{getJobStatusChip(app.jobStatus)}</TableCell>
                      <TableCell>{getStatusChip(app.status)}</TableCell>
                      <TableCell>
                        {(() => {
                          const score = getApplicationScore(app);
                          return score !== undefined ? (
                            <Chip
                              label={`${score}%`}
                              size="small"
                              color={score >= 80 ? 'success' : score >= 60 ? 'warning' : 'default'}
                            />
                          ) : (
                            <Typography variant="body2" color="text.secondary">-</Typography>
                          );
                        })()}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {new Date(app.submittedAt).toLocaleDateString()}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleViewApplication(app)}
                            >
                              <ViewIcon />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Download Resume">
                            <IconButton
                              size="small"
                              color="info"
                              onClick={() => handleDownloadResume(app.resumeUrl, app.applicantName)}
                            >
                              <DownloadIcon />
                            </IconButton>
                          </Tooltip>

                          {app.status !== 'hired' && app.status !== 'rejected' && (
                            <Tooltip title="Update Status">
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => handleStatusChange(app)}
                              >
                                <ApproveIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </TableContainer>
        </Paper>
      </Container>

      {/* Application Detail Dialog */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        {selectedApplication && (
          <>
            <DialogTitle>
              <Typography variant="h5" fontWeight={700}>
                Application Details
              </Typography>
            </DialogTitle>
            <DialogContent dividers>
              <Stack spacing={3}>
                {/* Applicant Info */}
                <Box>
                  <Typography variant="h6" gutterBottom>Applicant Information</Typography>
                  <Stack spacing={1}>
                    <Typography><strong>Name:</strong> {selectedApplication.applicantName}</Typography>
                    <Typography><strong>Email:</strong> {selectedApplication.applicantEmail}</Typography>
                    {selectedApplication.applicantPhone && (
                      <Typography><strong>Phone:</strong> {selectedApplication.applicantPhone}</Typography>
                    )}
                  </Stack>
                </Box>

                <Divider />

                {/* Job Info */}
                <Box>
                  <Typography variant="h6" gutterBottom>Job Applied For</Typography>
                  <Typography>{selectedApplication.jobTitle}</Typography>
                </Box>

                <Divider />

                {/* Cover Letter */}
                {selectedApplication.coverLetter && (
                  <>
                    <Box>
                      <Typography variant="h6" gutterBottom>Cover Letter</Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {selectedApplication.coverLetter}
                      </Typography>
                    </Box>
                    <Divider />
                  </>
                )}

                {/* Status */}
                <Box>
                  <Typography variant="h6" gutterBottom>Application Status</Typography>
                  <Stack direction="row" spacing={2} alignItems="center">
                    {getStatusChip(selectedApplication.status)}
                    {(() => {
                      const score = getApplicationScore(selectedApplication);
                      return score !== undefined && (
                        <Chip label={`Match: ${score}%`} color="primary" />
                      );
                    })()}
                  </Stack>
                  {selectedApplication.reviewedBy && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Reviewed by {selectedApplication.reviewedBy} on{' '}
                      {new Date(selectedApplication.reviewedAt!).toLocaleString()}
                    </Typography>
                  )}
                </Box>

                {/* Screening Results */}
                {selectedApplication.screeningResults && (
                  <>
                    <Divider />
                    <Box>
                      <Typography variant="h6" gutterBottom>Resume Analysis</Typography>

                      {/* Overall Scores - Handle both direct and nested structure */}
                      {(() => {
                        const results = selectedApplication.screeningResults;
                        const matchResult = results.matchResult || results;
                        const overallScore = matchResult.overallScore || 0;
                        const experienceMatch = matchResult.experienceMatch || 0;
                        const educationMatch = matchResult.educationMatch || 0;

                        return (
                          <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                            <Chip
                              label={`Overall: ${overallScore}%`}
                              color={
                                overallScore >= 70
                                  ? 'success'
                                  : overallScore >= 40
                                  ? 'warning'
                                  : 'error'
                              }
                            />
                            {experienceMatch > 0 && (
                              <Chip
                                label={`Experience: ${experienceMatch}%`}
                                variant="outlined"
                                color="primary"
                              />
                            )}
                            {educationMatch > 0 && (
                              <Chip
                                label={`Education: ${educationMatch}%`}
                                variant="outlined"
                                color="primary"
                              />
                            )}
                          </Stack>
                        );
                      })()}

                      {/* Skill Matches */}
                      {(() => {
                        const results = selectedApplication.screeningResults;
                        const matchResult = results.matchResult || results;
                        const skillMatches = matchResult.skillMatches || [];
                        return skillMatches.length > 0 && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="subtitle2" gutterBottom>
                            Skill Matches
                          </Typography>
                          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                            {skillMatches.map((skillMatch: any, index: number) => (
                              <Chip
                                key={index}
                                label={skillMatch.skill || skillMatch}
                                size="small"
                                color={skillMatch.matched ? 'success' : 'default'}
                                icon={skillMatch.matched ? <CheckCircle fontSize="small" /> : undefined}
                                variant={skillMatch.matched ? 'filled' : 'outlined'}
                              />
                            ))}
                          </Stack>
                        </Box>
                        );
                      })()}

                      {/* Skills from parsedResume */}
                      {(() => {
                        const results = selectedApplication.screeningResults;
                        const skills = results.skills || results.parsedResume?.skills || [];
                        return skills.length > 0 && (
                        <Box>
                          <Typography variant="subtitle2" gutterBottom>
                            Detected Skills
                          </Typography>
                          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                            {skills.map((skill: any, index: number) => (
                              <Chip key={index} label={skill} size="small" variant="outlined" />
                            ))}
                          </Stack>
                        </Box>
                        );
                      })()}
                    </Box>
                  </>
                )}

                {/* AI Analysis */}
                <Divider />
                <Box>
                  <AIResumeAnalysis
                    analysisResult={aiAnalysis || undefined}
                    onAnalyze={handleAIAnalysis}
                    loading={aiLoading}
                  />
                </Box>

                {/* Notes & Activity */}
                <Divider />
                <Box>
                  {currentUser && (
                    <NotesChat
                      applicationId={selectedApplication.id}
                      entityId={currentUser.entityId}
                      currentUserId={currentUser.id}
                      currentUserName={currentUser.name || currentUser.email}
                      currentUserRole={currentUser.role || 'user'}
                      maxHeight={250}
                    />
                  )}
                </Box>
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDetailDialogOpen(false)}>Close</Button>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={() => handleDownloadResume(selectedApplication.resumeUrl, selectedApplication.applicantName)}
              >
                Download Resume
              </Button>
              {selectedApplication.applicantPhone && (
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={aiCallLoading ? <CircularProgress size={20} color="inherit" /> : <SmartToyIcon />}
                  onClick={handleAICallFromDialog}
                  disabled={aiCallLoading}
                  sx={{ px: 3 }}
                >
                  {aiCallLoading ? 'Calling...' : 'AI Call'}
                </Button>
              )}
              {isAIEnabled() && !aiLoading && (
                <Button
                  variant={aiAnalysis ? "outlined" : "contained"}
                  color="primary"
                  startIcon={<AIIcon />}
                  onClick={handleAIAnalysis}
                >
                  {aiAnalysis ? 'Re-analyze with AI' : 'Analyze with AI'}
                </Button>
              )}
              {selectedApplication.status !== 'hired' && selectedApplication.status !== 'rejected' && (
                <Button
                  variant="contained"
                  onClick={() => {
                    setDetailDialogOpen(false);
                    handleStatusChange(selectedApplication);
                  }}
                >
                  Update Status
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Status Change Dialog */}
      <Dialog open={statusDialogOpen} onClose={() => setStatusDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Update Application Status</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            {error && (
              <Alert severity="error" onClose={() => setError('')}>
                {error}
              </Alert>
            )}
            <TextField
              select
              fullWidth
              label="New Status"
              value={newStatus}
              onChange={(e) => setNewStatus(e.target.value)}
            >
              <MenuItem value="reviewing">Under Review</MenuItem>
              <MenuItem value="shortlisted">Shortlisted</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
              <MenuItem value="hired">Hired</MenuItem>
            </TextField>

            <TextField
              fullWidth
              multiline
              rows={4}
              label="Notes (optional)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any notes about this decision..."
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialogOpen(false)} disabled={statusUpdateLoading}>
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            loading={statusUpdateLoading}
            loadingText="Updating..."
            onClick={handleUpdateStatus}
          >
            Update Status
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
